import {
  Container,
  Typography,
  Button,
  Input,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import React, { useState, useEffect, useContext } from "react";
import OwnerHeader from "./Components/OwnerHeader";
import { useParams } from "react-router-dom";
import { Instance } from "../../Config";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import GlobalContext from "../../Context/globalContext";
import { SHOW_SNACKBAR } from "../../Context/globalActions.types";
import IOSSwitch from "../../Components/IOSSwitch";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const RULE_TYPE_LABELS = {
  instant_closure: "Instant",
  date_closure: "Scheduled",
  vacation: "Vacation",
  weekly_hours: "Weekly Hours",
  time_closure: "Time Closure",
  capacity_limit: "Capacity",
};

const RULE_TYPE_COLORS = {
  instant_closure: "bg-red-100 text-red-700",
  date_closure: "bg-blue-100 text-blue-700",
  vacation: "bg-purple-100 text-purple-700",
  weekly_hours: "bg-teal-100 text-teal-700",
  time_closure: "bg-orange-100 text-orange-700",
  capacity_limit: "bg-yellow-100 text-yellow-700",
};

const RULE_TYPE_HELPERS = {
  date_closure:
    "Close your shop for specific dates. Use this for planned closures like holidays, renovations, or special events.",
  vacation:
    "Mark your shop as on vacation for a date range. Customers will see a vacation message instead of regular closure.",
  weekly_hours:
    "Set your regular operating hours. Your shop will automatically close outside these hours and on non-operating days.",
  time_closure:
    "Close your shop during specific hours of the day. Useful for lunch breaks or preparation time.",
  capacity_limit:
    "Automatically stop accepting orders when a maximum number of orders per day or per hour is reached.",
};

const APPLIES_TO_OPTIONS = [
  { value: "all", label: "All Users" },
  { value: "user", label: "Customer" },
  { value: "distributor", label: "Distributor" },
  { value: "salesman", label: "Salesman" },
];

const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: "#111827",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    padding: "10px 14px",
  },
  body: {
    fontSize: 13,
    padding: "10px 14px",
  },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
  root: {
    "&:nth-of-type(even)": {
      backgroundColor: "#FBFBFB",
    },
    "&:hover": {
      backgroundColor: "#F3F3F3",
    },
  },
}))(TableRow);

const ShopClosure = ({ embedded = false }) => {
  const params = useParams();
  const { dispatch } = useContext(GlobalContext);

  const [isLoading, setIsLoading] = useState(true);
  const [shopStatus, setShopStatus] = useState(null);
  const [allRules, setAllRules] = useState([]);

  // Close shop dialog
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeMessage, setCloseMessage] = useState("");
  const [closeAcceptOrders, setCloseAcceptOrders] = useState(false);
  const [closeNotify, setCloseNotify] = useState(true);
  const [closeAppliesTo, setCloseAppliesTo] = useState("all");
  const [closeTargetGroups, setCloseTargetGroups] = useState([]);
  const [isClosing, setIsClosing] = useState(false);

  // Open shop
  const [openNotify, setOpenNotify] = useState(true);
  const [isOpening, setIsOpening] = useState(false);

  // Create rule dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createType, setCreateType] = useState("date_closure");
  const [formTitle, setFormTitle] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [formStartDate, setFormStartDate] = useState("");
  const [formEndDate, setFormEndDate] = useState("");
  const [formStartTime, setFormStartTime] = useState("");
  const [formEndTime, setFormEndTime] = useState("");
  const [formDayOfWeek, setFormDayOfWeek] = useState([]);
  const [formAcceptOrders, setFormAcceptOrders] = useState(false);
  const [formNotify, setFormNotify] = useState(true);
  const [formPriority, setFormPriority] = useState(0);
  const [formMaxOrdersPerDay, setFormMaxOrdersPerDay] = useState("");
  const [formMaxOrdersPerHour, setFormMaxOrdersPerHour] = useState("");
  const [formAppliesTo, setFormAppliesTo] = useState("all");
  const [formTargetGroups, setFormTargetGroups] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editDayOfWeek, setEditDayOfWeek] = useState([]);
  const [editAcceptOrders, setEditAcceptOrders] = useState(false);
  const [editPriority, setEditPriority] = useState(0);
  const [editMaxOrdersPerDay, setEditMaxOrdersPerDay] = useState("");
  const [editMaxOrdersPerHour, setEditMaxOrdersPerHour] = useState("");
  const [editAppliesTo, setEditAppliesTo] = useState("all");
  const [editTargetGroups, setEditTargetGroups] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Available group codes from API
  const [availableGroups, setAvailableGroups] = useState([]);

  const showSnackbar = (message, severity = "success") => {
    dispatch({ type: SHOW_SNACKBAR, payload: { message, severity } });
  };

  const fetchStatus = async () => {
    try {
      const res = await Instance.get(
        `/shop-closure/status?mobileNo=${params.mobileNo}`
      );
      setShopStatus(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRules = async () => {
    try {
      const res = await Instance.get(
        `/shop-closure/rules?mobileNo=${params.mobileNo}`
      );
      setAllRules(res.data.result || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await Instance.get(
        `/shop-closure/groups?mobileNo=${params.mobileNo}`
      );
      setAvailableGroups(res.data.result || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    await Promise.all([fetchStatus(), fetchRules(), fetchGroups()]);
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Close shop instantly
  const handleCloseShop = async () => {
    if (!closeMessage.trim()) {
      showSnackbar("Please enter a closed message", "error");
      return;
    }
    setIsClosing(true);
    try {
      const payload = {
        mobileNo: params.mobileNo,
        message: closeMessage,
        acceptOrders: closeAcceptOrders,
        notifyCustomers: closeNotify,
        appliesTo: closeAppliesTo,
      };
      if (closeTargetGroups.length > 0) {
        payload.targetGroups = closeTargetGroups;
      }
      await Instance.post("/shop-closure/close", payload);
      showSnackbar("Shop has been closed");
      setCloseMessage("");
      setCloseAcceptOrders(false);
      setCloseNotify(true);
      setCloseAppliesTo("all");
      setCloseTargetGroups([]);
      setCloseDialogOpen(false);
      await loadData();
    } catch (err) {
      showSnackbar("Failed to close shop", "error");
    }
    setIsClosing(false);
  };

  // Open shop
  const handleOpenShop = async () => {
    setIsOpening(true);
    try {
      await Instance.post("/shop-closure/open", {
        mobileNo: params.mobileNo,
        notifyCustomers: openNotify,
      });
      showSnackbar("Shop has been opened");
      await loadData();
    } catch (err) {
      showSnackbar("Failed to open shop", "error");
    }
    setIsOpening(false);
  };

  // Create rule
  const handleCreate = async () => {
    if (createType === "capacity_limit") {
      if (!formMaxOrdersPerDay && !formMaxOrdersPerHour) {
        showSnackbar("Please set at least one capacity limit", "error");
        return;
      }
    } else if (createType === "weekly_hours") {
      if (formDayOfWeek.length === 0 || !formStartTime || !formEndTime) {
        showSnackbar("Please select days and set open hours", "error");
        return;
      }
    } else if (createType === "time_closure") {
      if (!formStartTime || !formEndTime) {
        showSnackbar("Please set start and end time", "error");
        return;
      }
    } else {
      if (!formTitle.trim() || !formStartDate) {
        showSnackbar("Please fill title and start date", "error");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const body = {
        mobileNo: params.mobileNo,
        ruleType: createType,
        title: formTitle || RULE_TYPE_LABELS[createType],
        message: formMessage || null,
        startDate: formStartDate || null,
        endDate: formEndDate || null,
        startTime: formStartTime || null,
        endTime: formEndTime || null,
        dayOfWeek: formDayOfWeek.length > 0 ? formDayOfWeek : null,
        acceptOrders: formAcceptOrders,
        notifyCustomers: formNotify,
        priority: formPriority,
        appliesTo: formAppliesTo,
      };

      if (formTargetGroups.length > 0) {
        body.targetGroups = formTargetGroups;
      }

      if (createType === "capacity_limit") {
        body.maxOrdersPerDay = formMaxOrdersPerDay
          ? parseInt(formMaxOrdersPerDay)
          : null;
        body.maxOrdersPerHour = formMaxOrdersPerHour
          ? parseInt(formMaxOrdersPerHour)
          : null;
      }

      await Instance.post("/shop-closure/rule", body);
      showSnackbar("Rule created successfully");
      resetCreateForm();
      setCreateDialogOpen(false);
      await loadData();
    } catch (err) {
      showSnackbar("Failed to create rule", "error");
    }
    setIsSubmitting(false);
  };

  const resetCreateForm = () => {
    setCreateType("date_closure");
    setFormTitle("");
    setFormMessage("");
    setFormStartDate("");
    setFormEndDate("");
    setFormStartTime("");
    setFormEndTime("");
    setFormDayOfWeek([]);
    setFormAcceptOrders(false);
    setFormNotify(true);
    setFormPriority(0);
    setFormMaxOrdersPerDay("");
    setFormMaxOrdersPerHour("");
    setFormAppliesTo("all");
    setFormTargetGroups([]);
  };

  // Delete rule
  const handleDelete = async (id) => {
    try {
      await Instance.delete(`/shop-closure/${id}`);
      showSnackbar("Rule removed");
      await loadData();
    } catch (err) {
      showSnackbar("Failed to delete", "error");
    }
  };

  // Open edit dialog
  const openEdit = (rule) => {
    setEditRule(rule);
    setEditTitle(rule.TITLE || "");
    setEditMessage(rule.MESSAGE || "");
    setEditStartDate(rule.START_DATE || "");
    setEditEndDate(rule.END_DATE || "");
    setEditStartTime(rule.START_TIME || "");
    setEditEndTime(rule.END_TIME || "");
    setEditDayOfWeek(
      rule.DAY_OF_WEEK ? rule.DAY_OF_WEEK.split(",").map((d) => d.trim()) : []
    );
    setEditAcceptOrders(rule.ACCEPT_ORDERS || false);
    setEditPriority(rule.PRIORITY || 0);

    let meta = {};
    try {
      meta = rule.META ? JSON.parse(rule.META) : {};
    } catch (_) {}
    setEditMaxOrdersPerDay(meta.maxOrdersPerDay || "");
    setEditMaxOrdersPerHour(meta.maxOrdersPerHour || "");
    setEditAppliesTo(rule.APPLIES_TO || "all");
    setEditTargetGroups(
      meta.target && meta.target.groups ? meta.target.groups : []
    );

    setEditOpen(true);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editTitle.trim()) {
      showSnackbar("Title is required", "error");
      return;
    }
    setIsSaving(true);
    try {
      const body = {
        title: editTitle,
        message: editMessage,
        startDate: editStartDate || null,
        endDate: editEndDate || null,
        startTime: editStartTime || null,
        endTime: editEndTime || null,
        dayOfWeek: editDayOfWeek.length > 0 ? editDayOfWeek : null,
        acceptOrders: editAcceptOrders,
        priority: editPriority,
        appliesTo: editAppliesTo,
      };

      body.targetGroups = editTargetGroups;

      if (editRule?.RULE_TYPE === "capacity_limit") {
        body.maxOrdersPerDay = editMaxOrdersPerDay
          ? parseInt(editMaxOrdersPerDay)
          : null;
        body.maxOrdersPerHour = editMaxOrdersPerHour
          ? parseInt(editMaxOrdersPerHour)
          : null;
      }

      await Instance.put(`/shop-closure/${editRule.id}`, body);
      showSnackbar("Rule updated");
      setEditOpen(false);
      await loadData();
    } catch (err) {
      showSnackbar("Failed to update", "error");
    }
    setIsSaving(false);
  };

  const toggleDay = (day, current, setter) => {
    if (current.includes(day)) {
      setter(current.filter((d) => d !== day));
    } else {
      setter([...current, day]);
    }
  };

  const isClosed = shopStatus?.isClosed;

  const getRuleDescription = (rule) => {
    switch (rule.RULE_TYPE) {
      case "weekly_hours":
        return `${rule.DAY_OF_WEEK || "\u2014"} | ${rule.START_TIME || ""} - ${rule.END_TIME || ""}`;
      case "time_closure":
        return `${rule.START_TIME || ""} - ${rule.END_TIME || ""}${rule.START_DATE ? ` (${formatDate(rule.START_DATE)})` : ""}`;
      case "capacity_limit": {
        let meta = {};
        try {
          meta = rule.META ? JSON.parse(rule.META) : {};
        } catch (_) {}
        const parts = [];
        if (meta.maxOrdersPerDay)
          parts.push(`${meta.maxOrdersPerDay}/day`);
        if (meta.maxOrdersPerHour)
          parts.push(`${meta.maxOrdersPerHour}/hour`);
        return parts.join(", ") || "\u2014";
      }
      default:
        return rule.MESSAGE || "\u2014";
    }
  };

  // Fields shown conditionally based on rule type
  const showDateFields = ["date_closure", "vacation", "time_closure"].includes(
    createType
  );
  const showTimeFields = ["weekly_hours", "time_closure"].includes(createType);
  const showDayFields = createType === "weekly_hours";
  const showCapacityFields = createType === "capacity_limit";

  const content = (
    <>
      <Container
        maxWidth="xl"
        className={embedded ? "pt-2 pb-7" : "pt-2 bg-[#F1F5F9] min-h-screen pb-7 px-3"}
      >
        {isLoading ? (
          <div className="flex justify-center py-16">
            <CircularProgress style={{ color: "#3333cc" }} />
          </div>
        ) : (
          <>
            {/* Status Bar */}
            <div className="bg-white shadow-sm rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: isClosed ? "#FEE2E2" : "#DCFCE7",
                    }}
                  >
                    <StorefrontOutlinedIcon
                      style={{
                        color: isClosed ? "#DC2626" : "#16A34A",
                        fontSize: 22,
                      }}
                    />
                  </div>
                  <div>
                    <Typography
                      variant="subtitle1"
                      className="font-bold"
                      style={{ color: isClosed ? "#DC2626" : "#16A34A" }}
                    >
                      {isClosed ? "Shop is Closed" : "Shop is Open"}
                    </Typography>
                    {isClosed && shopStatus?.message && (
                      <Typography variant="body2" className="text-gray-500">
                        {shopStatus.message}
                      </Typography>
                    )}
                    {isClosed && shopStatus?.acceptOrders && (
                      <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                        Accepting Orders
                      </span>
                    )}
                    {isClosed && shopStatus?.capacityReached && (
                      <span className="inline-block mt-1 ml-1 text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                        Capacity Reached
                      </span>
                    )}
                  </div>
                </div>

                {isClosed ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Typography variant="body2" className="text-sm">
                        Notify
                      </Typography>
                      <IOSSwitch
                        isChecked={openNotify}
                        handleChange={(e) => setOpenNotify(e.target.checked)}
                        name="openNotify"
                      />
                    </div>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleOpenShop}
                      disabled={isOpening}
                      style={{
                        backgroundColor: isOpening ? undefined : "#16A34A",
                        color: "#fff",
                      }}
                    >
                      {isOpening ? (
                        <CircularProgress size={20} className="text-white" />
                      ) : (
                        "Open Shop"
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setCloseDialogOpen(true)}
                    style={{ borderColor: "#DC2626", color: "#DC2626" }}
                  >
                    Close Shop
                  </Button>
                )}
              </div>
            </div>

            {/* Rules Table */}
            <div className="bg-white shadow-sm rounded-lg mb-4">
              <div className="px-4 pt-3 pb-2 flex items-center justify-between flex-wrap gap-2">
                <Typography variant="subtitle1" className="font-bold">
                  Availability Rules ({allRules.length})
                </Typography>
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    resetCreateForm();
                    setCreateDialogOpen(true);
                  }}
                  style={{ backgroundColor: "#3333cc", color: "#fff" }}
                >
                  Create Rule
                </Button>
              </div>

              {allRules.length === 0 ? (
                <div className="px-4 pb-6 pt-2 text-center">
                  <StorefrontOutlinedIcon
                    style={{ fontSize: 48, color: "#D1D5DB" }}
                  />
                  <Typography
                    variant="body2"
                    className="text-gray-400 mt-2"
                  >
                    No rules created yet. Create a rule to manage your shop
                    availability.
                  </Typography>
                </div>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Title</StyledTableCell>
                        <StyledTableCell>Type</StyledTableCell>
                        <StyledTableCell>Applies To</StyledTableCell>
                        <StyledTableCell>Details</StyledTableCell>
                        <StyledTableCell>From</StyledTableCell>
                        <StyledTableCell>To</StyledTableCell>
                        <StyledTableCell>Accept Orders</StyledTableCell>
                        <StyledTableCell>Priority</StyledTableCell>
                        <StyledTableCell>Status</StyledTableCell>
                        <StyledTableCell align="center">
                          Actions
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allRules.map((rule) => (
                        <StyledTableRow key={rule.id}>
                          <StyledTableCell>
                            {rule.TITLE || "\u2014"}
                          </StyledTableCell>
                          <StyledTableCell>
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                RULE_TYPE_COLORS[rule.RULE_TYPE] ||
                                "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {RULE_TYPE_LABELS[rule.RULE_TYPE] ||
                                rule.RULE_TYPE}
                            </span>
                          </StyledTableCell>
                          <StyledTableCell>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                              {APPLIES_TO_OPTIONS.find(
                                (o) => o.value === rule.APPLIES_TO
                              )?.label || "All Users"}
                            </span>
                            {(() => {
                              try {
                                const meta = rule.META ? JSON.parse(rule.META) : {};
                                if (meta.target?.groups?.length > 0) {
                                  return (
                                    <div className="mt-1 text-[10px] text-gray-500">
                                      Groups: {meta.target.groups.join(", ")}
                                    </div>
                                  );
                                }
                              } catch (_) {}
                              return null;
                            })()}
                          </StyledTableCell>
                          <StyledTableCell>
                            <span className="line-clamp-2 text-xs text-gray-600">
                              {getRuleDescription(rule)}
                            </span>
                          </StyledTableCell>
                          <StyledTableCell>
                            {formatDate(rule.START_DATE) || "\u2014"}
                          </StyledTableCell>
                          <StyledTableCell>
                            {formatDate(rule.END_DATE) || "\u2014"}
                          </StyledTableCell>
                          <StyledTableCell>
                            {rule.ACCEPT_ORDERS ? (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                Yes
                              </span>
                            ) : (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                No
                              </span>
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            {rule.PRIORITY || 0}
                          </StyledTableCell>
                          <StyledTableCell>
                            {rule.IS_ACTIVE ? (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                Active
                              </span>
                            ) : (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                Inactive
                              </span>
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => openEdit(rule)}
                              style={{ color: "#3333cc" }}
                            >
                              <EditOutlinedIcon style={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(rule.id)}
                              style={{ color: "#EF4444" }}
                            >
                              <DeleteOutlineOutlinedIcon
                                style={{ fontSize: 18 }}
                              />
                            </IconButton>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          </>
        )}
      </Container>

      {/* Close Shop Dialog */}
      <Dialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="bg-[#3333CC]">
          <div className="flex justify-between text-white">
            <div>Close Shop Now</div>
            <div
              onClick={() => setCloseDialogOpen(false)}
              className="cursor-pointer"
            >
              <CloseIcon />
            </div>
          </div>
        </DialogTitle>
        <DialogContent className="pt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 mt-2 flex gap-2">
            <InfoOutlinedIcon style={{ fontSize: 18, color: "#DC2626", marginTop: 2 }} />
            <Typography variant="body2" className="text-red-700 text-sm">
              This will instantly close your shop. Customers will see a closed message. You can reopen anytime.
            </Typography>
          </div>

          <div className="mb-3">
            <Typography variant="body2" className="font-semibold mb-1">
              Closed Message *
            </Typography>
            <Input
              fullWidth
              disableUnderline
              multiline
              rows={2}
              placeholder="e.g. Closed for maintenance"
              value={closeMessage}
              onChange={(e) => setCloseMessage(e.target.value)}
              className="bg-white border-[1px] border-[#3333cc] px-2 py-1 rounded-lg w-full"
            />
          </div>

          {/* Applies To */}
          <div className="mb-3">
            <Typography variant="body2" className="font-semibold mb-1">
              Close For
            </Typography>
            <select
              value={closeAppliesTo}
              onChange={(e) => setCloseAppliesTo(e.target.value)}
              className="w-full border-[1px] border-[#3333cc] px-2 py-2 rounded-lg text-sm outline-none bg-white"
            >
              {APPLIES_TO_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Target Groups */}
          {availableGroups.length > 0 && (
            <div className="mb-3">
              <Typography variant="body2" className="font-semibold mb-1">
                Target Groups
              </Typography>
              <div className="text-xs text-gray-500 mb-1">
                Optionally restrict to specific account groups. Leave empty to apply to all groups.
              </div>
              <div className="flex flex-wrap gap-1">
                {availableGroups.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() =>
                      setCloseTargetGroups((prev) =>
                        prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
                      )
                    }
                    className={`px-3 py-1 rounded-full text-xs border ${
                      closeTargetGroups.includes(g)
                        ? "bg-[#3333cc] text-white border-[#3333cc]"
                        : "bg-white text-gray-700 border-gray-300"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center mb-2">
            <Typography variant="body2">
              Accept Orders While Closed
            </Typography>
            <IOSSwitch
              isChecked={closeAcceptOrders}
              handleChange={(e) =>
                setCloseAcceptOrders(e.target.checked)
              }
              name="closeAcceptOrders"
            />
          </div>

          <div className="flex justify-between items-center mb-1">
            <Typography variant="body2">Notify Customers</Typography>
            <IOSSwitch
              isChecked={closeNotify}
              handleChange={(e) => setCloseNotify(e.target.checked)}
              name="closeNotify"
            />
          </div>
        </DialogContent>
        <DialogActions className="px-4 pb-3">
          <Button
            variant="outlined"
            onClick={() => setCloseDialogOpen(false)}
            style={{ borderColor: "#ccc", color: "#333" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleCloseShop}
            disabled={isClosing}
            style={{
              backgroundColor: isClosing ? undefined : "#DC2626",
              color: "#fff",
            }}
          >
            {isClosing ? (
              <CircularProgress size={20} className="text-white" />
            ) : (
              "Close Shop"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Rule Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="bg-[#3333CC]">
          <div className="flex justify-between text-white">
            <div>Create Availability Rule</div>
            <div
              onClick={() => setCreateDialogOpen(false)}
              className="cursor-pointer"
            >
              <CloseIcon />
            </div>
          </div>
        </DialogTitle>
        <DialogContent className="pt-4">
          {/* Rule Type */}
          <div className="mb-3 mt-2">
            <Typography variant="body2" className="font-semibold mb-1">
              Rule Type
            </Typography>
            <select
              value={createType}
              onChange={(e) => {
                setCreateType(e.target.value);
                setFormTitle("");
                setFormMessage("");
                setFormStartDate("");
                setFormEndDate("");
                setFormStartTime("");
                setFormEndTime("");
                setFormDayOfWeek([]);
                setFormAcceptOrders(false);
                setFormNotify(true);
                setFormPriority(0);
                setFormMaxOrdersPerDay("");
                setFormMaxOrdersPerHour("");
              }}
              className="w-full border-[1px] border-[#3333cc] px-2 py-2 rounded-lg text-sm outline-none bg-white"
            >
              <option value="date_closure">Scheduled Closure</option>
              <option value="vacation">Vacation</option>
              <option value="weekly_hours">Weekly Hours</option>
              <option value="time_closure">Time Closure</option>
              <option value="capacity_limit">Capacity Limit</option>
            </select>
          </div>

          {/* Helper message for the selected rule type */}
          {RULE_TYPE_HELPERS[createType] && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex gap-2">
              <InfoOutlinedIcon style={{ fontSize: 18, color: "#3333cc", marginTop: 2 }} />
              <Typography variant="body2" className="text-blue-800 text-sm">
                {RULE_TYPE_HELPERS[createType]}
              </Typography>
            </div>
          )}

          {/* Title */}
          <div className="mb-3">
            <Typography variant="body2" className="font-semibold mb-1">
              Title
            </Typography>
            <Input
              fullWidth
              disableUnderline
              placeholder="e.g. Diwali Holiday"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              className="bg-white border-[1px] border-[#3333cc] px-2 py-1 rounded-lg w-full"
            />
          </div>

          {/* Message (not for capacity) */}
          {!showCapacityFields && (
            <div className="mb-3">
              <Typography
                variant="body2"
                className="font-semibold mb-1"
              >
                Message
              </Typography>
              <Input
                fullWidth
                disableUnderline
                multiline
                rows={2}
                placeholder="e.g. Closed for Diwali. Back on Nov 3!"
                value={formMessage}
                onChange={(e) => setFormMessage(e.target.value)}
                className="bg-white border-[1px] border-[#3333cc] px-2 py-1 rounded-lg w-full"
              />
            </div>
          )}

          {/* Day of Week */}
          {showDayFields && (
            <div className="mb-3">
              <Typography
                variant="body2"
                className="font-semibold mb-1"
              >
                Operating Days
              </Typography>
              <div className="flex flex-wrap gap-1.5">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() =>
                      toggleDay(day, formDayOfWeek, setFormDayOfWeek)
                    }
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      formDayOfWeek.includes(day)
                        ? "bg-[#3333cc] text-white border-[#3333cc]"
                        : "bg-white text-gray-600 border-gray-300 hover:border-[#3333cc]"
                    }`}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date Fields */}
          {showDateFields && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Typography
                  variant="body2"
                  className="font-semibold mb-1"
                >
                  From Date
                </Typography>
                <input
                  type="date"
                  value={formStartDate}
                  onChange={(e) => setFormStartDate(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
              <div>
                <Typography
                  variant="body2"
                  className="font-semibold mb-1"
                >
                  To Date
                </Typography>
                <input
                  type="date"
                  value={formEndDate}
                  onChange={(e) => setFormEndDate(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
            </div>
          )}

          {/* Time Fields */}
          {showTimeFields && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Typography
                  variant="body2"
                  className="font-semibold mb-1"
                >
                  {createType === "weekly_hours"
                    ? "Open From"
                    : "Close From"}
                </Typography>
                <input
                  type="time"
                  value={formStartTime}
                  onChange={(e) => setFormStartTime(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
              <div>
                <Typography
                  variant="body2"
                  className="font-semibold mb-1"
                >
                  {createType === "weekly_hours"
                    ? "Open Until"
                    : "Close Until"}
                </Typography>
                <input
                  type="time"
                  value={formEndTime}
                  onChange={(e) => setFormEndTime(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
            </div>
          )}

          {/* Capacity Fields */}
          {showCapacityFields && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Typography
                  variant="body2"
                  className="font-semibold mb-1"
                >
                  Max Orders / Day
                </Typography>
                <input
                  type="number"
                  min="0"
                  value={formMaxOrdersPerDay}
                  onChange={(e) =>
                    setFormMaxOrdersPerDay(e.target.value)
                  }
                  placeholder="e.g. 100"
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
              <div>
                <Typography
                  variant="body2"
                  className="font-semibold mb-1"
                >
                  Max Orders / Hour
                </Typography>
                <input
                  type="number"
                  min="0"
                  value={formMaxOrdersPerHour}
                  onChange={(e) =>
                    setFormMaxOrdersPerHour(e.target.value)
                  }
                  placeholder="e.g. 20"
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
            </div>
          )}

          {/* Applies To */}
          <div className="mb-3">
            <Typography variant="body2" className="font-semibold mb-1">
              Applies To
            </Typography>
            <select
              value={formAppliesTo}
              onChange={(e) => setFormAppliesTo(e.target.value)}
              className="w-full border-[1px] border-[#3333cc] px-2 py-2 rounded-lg text-sm outline-none bg-white"
            >
              {APPLIES_TO_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Typography
              variant="caption"
              className="text-gray-400 mt-0.5"
            >
              Choose which user role this rule applies to
            </Typography>
          </div>

          {/* Target Groups */}
          {availableGroups.length > 0 && (
            <div className="mb-3">
              <Typography variant="body2" className="font-semibold mb-1">
                Target Groups
              </Typography>
              <div className="text-xs text-gray-500 mb-1">
                Optionally restrict to specific account groups. Leave empty to apply to all groups.
              </div>
              <div className="flex flex-wrap gap-1">
                {availableGroups.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() =>
                      setFormTargetGroups((prev) =>
                        prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
                      )
                    }
                    className={`px-3 py-1 rounded-full text-xs border ${
                      formTargetGroups.includes(g)
                        ? "bg-[#3333cc] text-white border-[#3333cc]"
                        : "bg-white text-gray-700 border-gray-300"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Priority */}
          <div className="mb-3">
            <Typography variant="body2" className="font-semibold mb-1">
              Priority
            </Typography>
            <input
              type="number"
              min="0"
              value={formPriority}
              onChange={(e) =>
                setFormPriority(parseInt(e.target.value) || 0)
              }
              className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
            />
            <Typography
              variant="caption"
              className="text-gray-400 mt-0.5"
            >
              Higher priority rules take precedence
            </Typography>
          </div>

          <div className="flex justify-between items-center mb-2">
            <Typography variant="body2">Accept Orders</Typography>
            <IOSSwitch
              isChecked={formAcceptOrders}
              handleChange={(e) =>
                setFormAcceptOrders(e.target.checked)
              }
              name="formAcceptOrders"
            />
          </div>

          <div className="flex justify-between items-center mb-1">
            <Typography variant="body2">Notify Customers</Typography>
            <IOSSwitch
              isChecked={formNotify}
              handleChange={(e) => setFormNotify(e.target.checked)}
              name="formNotify"
            />
          </div>
        </DialogContent>
        <DialogActions className="px-4 pb-3">
          <Button
            variant="outlined"
            onClick={() => setCreateDialogOpen(false)}
            style={{ borderColor: "#ccc", color: "#333" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleCreate}
            disabled={isSubmitting}
            style={{
              backgroundColor: isSubmitting ? undefined : "#3333cc",
              color: "#fff",
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} className="text-white" />
            ) : (
              "Create Rule"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="bg-[#3333CC]">
          <div className="flex justify-between text-white">
            <div>
              Edit Rule
              {editRule && (
                <span
                  className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${
                    RULE_TYPE_COLORS[editRule.RULE_TYPE] ||
                    "bg-gray-100 text-gray-700"
                  }`}
                >
                  {RULE_TYPE_LABELS[editRule.RULE_TYPE] ||
                    editRule.RULE_TYPE}
                </span>
              )}
            </div>
            <div
              onClick={() => setEditOpen(false)}
              className="cursor-pointer"
            >
              <CloseIcon />
            </div>
          </div>
        </DialogTitle>
        <DialogContent className="pt-4">
          {/* Helper message for the edit rule type */}
          {editRule && RULE_TYPE_HELPERS[editRule.RULE_TYPE] && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 mt-2 flex gap-2">
              <InfoOutlinedIcon style={{ fontSize: 18, color: "#3333cc", marginTop: 2 }} />
              <Typography variant="body2" className="text-blue-800 text-sm">
                {RULE_TYPE_HELPERS[editRule.RULE_TYPE]}
              </Typography>
            </div>
          )}

          <div className="mb-3 mt-2">
            <Typography variant="body2" className="font-semibold mb-1">
              Title
            </Typography>
            <Input
              fullWidth
              disableUnderline
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="bg-white border-[1px] border-[#3333cc] px-2 py-1 rounded-lg w-full"
            />
          </div>

          {editRule?.RULE_TYPE !== "capacity_limit" && (
            <div className="mb-3">
              <Typography variant="body2" className="font-semibold mb-1">
                Message
              </Typography>
              <Input
                fullWidth
                disableUnderline
                multiline
                rows={2}
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                className="bg-white border-[1px] border-[#3333cc] px-2 py-1 rounded-lg w-full"
              />
            </div>
          )}

          {/* Day of Week for weekly_hours */}
          {editRule?.RULE_TYPE === "weekly_hours" && (
            <div className="mb-3">
              <Typography variant="body2" className="font-semibold mb-1">
                Operating Days
              </Typography>
              <div className="flex flex-wrap gap-1.5">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() =>
                      toggleDay(day, editDayOfWeek, setEditDayOfWeek)
                    }
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      editDayOfWeek.includes(day)
                        ? "bg-[#3333cc] text-white border-[#3333cc]"
                        : "bg-white text-gray-600 border-gray-300 hover:border-[#3333cc]"
                    }`}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date fields for date-based rules */}
          {["date_closure", "vacation", "time_closure"].includes(
            editRule?.RULE_TYPE
          ) && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Typography variant="body2" className="font-semibold mb-1">
                  From Date
                </Typography>
                <input
                  type="date"
                  value={editStartDate}
                  onChange={(e) => setEditStartDate(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
              <div>
                <Typography variant="body2" className="font-semibold mb-1">
                  To Date
                </Typography>
                <input
                  type="date"
                  value={editEndDate}
                  onChange={(e) => setEditEndDate(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
            </div>
          )}

          {/* Time fields for time-based rules */}
          {["weekly_hours", "time_closure"].includes(editRule?.RULE_TYPE) && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Typography variant="body2" className="font-semibold mb-1">
                  {editRule?.RULE_TYPE === "weekly_hours"
                    ? "Open From"
                    : "Close From"}
                </Typography>
                <input
                  type="time"
                  value={editStartTime}
                  onChange={(e) => setEditStartTime(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
              <div>
                <Typography variant="body2" className="font-semibold mb-1">
                  {editRule?.RULE_TYPE === "weekly_hours"
                    ? "Open Until"
                    : "Close Until"}
                </Typography>
                <input
                  type="time"
                  value={editEndTime}
                  onChange={(e) => setEditEndTime(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
            </div>
          )}

          {/* Capacity fields */}
          {editRule?.RULE_TYPE === "capacity_limit" && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Typography variant="body2" className="font-semibold mb-1">
                  Max Orders / Day
                </Typography>
                <input
                  type="number"
                  min="0"
                  value={editMaxOrdersPerDay}
                  onChange={(e) => setEditMaxOrdersPerDay(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
              <div>
                <Typography variant="body2" className="font-semibold mb-1">
                  Max Orders / Hour
                </Typography>
                <input
                  type="number"
                  min="0"
                  value={editMaxOrdersPerHour}
                  onChange={(e) => setEditMaxOrdersPerHour(e.target.value)}
                  className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
                />
              </div>
            </div>
          )}

          {/* Applies To */}
          <div className="mb-3">
            <Typography variant="body2" className="font-semibold mb-1">
              Applies To
            </Typography>
            <select
              value={editAppliesTo}
              onChange={(e) => setEditAppliesTo(e.target.value)}
              className="w-full border-[1px] border-[#3333cc] px-2 py-2 rounded-lg text-sm outline-none bg-white"
            >
              {APPLIES_TO_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Target Groups */}
          {availableGroups.length > 0 && (
            <div className="mb-3">
              <Typography variant="body2" className="font-semibold mb-1">
                Target Groups
              </Typography>
              <div className="text-xs text-gray-500 mb-1">
                Optionally restrict to specific account groups. Leave empty to apply to all groups.
              </div>
              <div className="flex flex-wrap gap-1">
                {availableGroups.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() =>
                      setEditTargetGroups((prev) =>
                        prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
                      )
                    }
                    className={`px-3 py-1 rounded-full text-xs border ${
                      editTargetGroups.includes(g)
                        ? "bg-[#3333cc] text-white border-[#3333cc]"
                        : "bg-white text-gray-700 border-gray-300"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Priority */}
          <div className="mb-3">
            <Typography variant="body2" className="font-semibold mb-1">
              Priority
            </Typography>
            <input
              type="number"
              min="0"
              value={editPriority}
              onChange={(e) =>
                setEditPriority(parseInt(e.target.value) || 0)
              }
              className="w-full border-[1px] border-[#3333cc] px-2 py-1.5 rounded-lg text-sm outline-none"
            />
          </div>

          <div className="flex justify-between items-center mb-2">
            <Typography variant="body2">Accept Orders</Typography>
            <IOSSwitch
              isChecked={editAcceptOrders}
              handleChange={(e) => setEditAcceptOrders(e.target.checked)}
              name="editAcceptOrders"
            />
          </div>
        </DialogContent>
        <DialogActions className="px-4 pb-3">
          <Button
            variant="outlined"
            onClick={() => setEditOpen(false)}
            style={{ borderColor: "#ccc", color: "#333" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSaveEdit}
            disabled={isSaving}
            style={{ backgroundColor: "#3333cc", color: "#fff" }}
          >
            {isSaving ? (
              <CircularProgress size={20} className="text-white" />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (embedded) return content;

  return (
    <OwnerHeader>
      <div>
        <div className="md:flex items-center justify-between px-3 md:px-4 py-3">
          <div>
            <div className="text-indigo-500 font-semibold text-sm md:text-base">
              Settings
            </div>
            <div className="text-xl md:text-2xl font-extrabold leading-7 sm:leading-10 truncate mt-1">
              Shop Availability
            </div>
          </div>
        </div>
        <div className="bg-[#F1F5F9] min-h-screen">
          {content}
        </div>
      </div>
    </OwnerHeader>
  );
};

export default ShopClosure;
